/**
 * TP on-chain recharge — legacy parity UI.
 * Min 500 TP (multiples of 500), rate from config, bound BEP20 wallet, USDT balance check.
 */
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  useAccount,
  useChainId,
  usePublicClient,
  useReadContract,
  useSwitchChain,
  useWalletClient,
} from 'wagmi';
import { bsc } from 'viem/chains';
import { erc20Abi, formatUnits, parseUnits, type Address, type Hash } from 'viem';
import {
  TP_RECHARGE_STEP,
  RP_RECHARGE_STEP,
  FC_RECHARGE_STEP,
  qualifiesForTpRechargeProfile,
} from '@uo/shared';
import { ApiError } from '../api/client';
import { rechargeAPI } from '../api/recharge';
import { usersAPI } from '../api/users';
import { useAuthStore, type UserProfile } from '../store/auth';
import { useWalletAuthLogin } from '../wallet/useWalletAuthLogin';
import {
  inflightUsdtWrites,
  waitForUsdtTransferOrHash,
} from '../wallet/wait-for-usdt-transfer';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import {
  THEMED_SURFACE_COLORS as C,
  ThemedBottomActionBar,
  ThemedField,
  ThemedInput,
  ThemedNotice,
  ThemedPanel,
} from '../components/ui/ThemedSurface';
import { BUYFUEL_COLORS } from '@/constants';

const PRESETS = ['500', '1000', '2000'];
const FC_PRESETS = ['1', '5', '10'];
const TARGET_CHAIN_ID = bsc.id;

function fmtNum(v: string | number, digits = 2): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

function normAddr(a: string): string {
  return a.trim().toLowerCase();
}

function RechargeOnchain({ asset }: { asset: 'tp' | 'rp' | 'fc' }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const label = asset.toUpperCase();
  const i18n = asset === 'tp' ? 'rechargeTp' : asset === 'rp' ? 'rechargeRp' : 'rechargeFc';
  const step =
    asset === 'tp' ? TP_RECHARGE_STEP : asset === 'rp' ? RP_RECHARGE_STEP : FC_RECHARGE_STEP;
  const qc = useQueryClient();
  const profile = useAuthStore((s) => s.profile);
  const wallet = useWalletAuthLogin();
  const { address, isConnected } = useAccount();
  const connectedChainId = useChainId();
  const publicClient = usePublicClient({ chainId: TARGET_CHAIN_ID });
  const { data: walletClient } = useWalletClient({ chainId: TARGET_CHAIN_ID });
  const { switchChainAsync, isPending: isSwitching } = useSwitchChain();

  const [amountTp, setAmountTp] = useState(asset === 'fc' ? '1' : '500');
  const [error, setError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<`0x${string}` | null>(null);
  const [manualTxHash, setManualTxHash] = useState('');
  const [showHashRescue, setShowHashRescue] = useState(false);
  const [pendingOrderId, setPendingOrderId] = useState<number | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const rechargeInflight = useRef<Promise<void> | null>(null);
  const submittedHashRef = useRef<string | null>(null);

  const { data: bal } = useQuery({
    queryKey: ['user', 'balances'],
    queryFn: () => usersAPI.getBalances(),
  });
  const assetBalance = bal?.balances?.[asset] ?? '0';

  const setProfile = useAuthStore((s) => s.setProfile);
  useQuery({
    queryKey: ['user', 'profile', `${asset}-recharge`],
    queryFn: async () => {
      const p = (await usersAPI.getProfile()) as UserProfile;
      if (p?.id) setProfile(p);
      return p;
    },
  });

  const configQuery = useQuery({
    queryKey: ['recharge', `${asset}-config`, TARGET_CHAIN_ID],
    queryFn: async () => {
      if (asset === 'tp') {
        return rechargeAPI.getTpOnchainConfig({ chain_id: TARGET_CHAIN_ID });
      }
      if (asset === 'fc') {
        const fc = await rechargeAPI.getFcOnchainConfig({ chain_id: TARGET_CHAIN_ID });
        return { ...fc, min_tp: fc.min_fc, tp_step: fc.fc_step };
      }
      const rp = await rechargeAPI.getRpOnchainConfig({ chain_id: TARGET_CHAIN_ID });
      return { ...rp, min_tp: rp.min_rp, tp_step: rp.rp_step };
    },
    retry: false,
  });
  const cfg = configQuery.data;
  const minTp = Number(cfg?.min_tp ?? (asset === 'fc' ? '1' : '500'));
  const rate = Number(
    cfg?.exchange_rate ?? (asset === 'tp' ? '1.14' : asset === 'fc' ? '13' : '1.105'),
  );
  const wrongChain = isConnected && connectedChainId !== TARGET_CHAIN_ID;

  /** Backend config already enforces A3+ (服务级). Prefer that over stale auth-store level_code (投资星级). FC 购买不限等级. */
  const tpRechargeEligible =
    asset === 'fc' ||
    configQuery.isSuccess ||
    qualifiesForTpRechargeProfile({
      level_code: profile?.level_code,
      reward_level_code: profile?.profile_metrics?.reward_level_code,
    });
  const levelGateBlocked =
    asset !== 'fc' &&
    configQuery.isError &&
    (configQuery.error instanceof ApiError
      ? /A3|level/i.test(configQuery.error.message)
      : false);

  const paymentUsdt = useMemo(() => {
    const tp = Number(amountTp);
    if (!Number.isFinite(tp) || tp <= 0) return '0';
    return (tp * rate).toFixed(2);
  }, [amountTp, rate]);

  const { data: usdtBalanceRaw } = useReadContract({
    address: (cfg?.usdt_contract ?? undefined) as Address | undefined,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: TARGET_CHAIN_ID,
    query: { enabled: Boolean(address && cfg?.usdt_contract) },
  });

  const usdtBalance = useMemo(() => {
    if (usdtBalanceRaw == null || cfg?.usdt_decimals == null) return '0';
    return formatUnits(usdtBalanceRaw, cfg.usdt_decimals);
  }, [usdtBalanceRaw, cfg?.usdt_decimals]);

  const confirmMut = useMutation({
    mutationFn: async (args: { hash: `0x${string}`; orderId?: number | null }) => {
      if (asset === 'tp') {
        return rechargeAPI.submitTpOnchain({
            amount_tp: amountTp.trim(),
            tx_hash: args.hash,
            chain_id: TARGET_CHAIN_ID,
            from_address: address!,
            wallet_config_id: cfg?.wallet?.id,
            order_id: args.orderId ?? pendingOrderId ?? undefined,
          });
      }
      if (asset === 'fc') {
        const fc = await rechargeAPI.submitFcOnchain({
          amount_fc: amountTp.trim(),
          tx_hash: args.hash,
          chain_id: TARGET_CHAIN_ID,
          from_address: address!,
          wallet_config_id: cfg?.wallet?.id,
          order_id: args.orderId ?? pendingOrderId ?? undefined,
        });
        return { ...fc, amount_tp: fc.amount_fc };
      }
      const rp = await rechargeAPI.submitRpOnchain({
        amount_rp: amountTp.trim(),
        tx_hash: args.hash,
        chain_id: TARGET_CHAIN_ID,
        from_address: address!,
        wallet_config_id: cfg?.wallet?.id,
        order_id: args.orderId ?? pendingOrderId ?? undefined,
      });
      return { ...rp, amount_tp: rp.amount_rp };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user', 'balances'] });
      void qc.invalidateQueries({ queryKey: ['recharge', `${asset}-records`] });
      setError(null);
      setShowHashRescue(false);
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : t(`${i18n}.submitFailed`, '充值确认失败'));
      setShowHashRescue(true);
    },
  });

  const isConfirming = confirmMut.isPending;

  async function submitHash(hash: `0x${string}`, orderId?: number | null) {
    if (submittedHashRef.current === hash && confirmMut.isSuccess) return;
    submittedHashRef.current = hash;
    setTxHash(hash);
    await confirmMut.mutateAsync({ hash, orderId });
  }

  async function createPendingOrder(): Promise<number> {
    if (asset === 'tp') {
      const p = await rechargeAPI.createTpPending({
        amount_tp: amountTp.trim(),
        chain_id: TARGET_CHAIN_ID,
      });
      return p.id;
    }
    if (asset === 'fc') {
      const p = await rechargeAPI.createFcPending({
        amount_fc: amountTp.trim(),
        chain_id: TARGET_CHAIN_ID,
      });
      return p.id;
    }
    const p = await rechargeAPI.createRpPending({
      amount_rp: amountTp.trim(),
      chain_id: TARGET_CHAIN_ID,
    });
    return p.id;
  }

  async function ensureWalletReady(): Promise<boolean> {
    if (!wallet.isConfigured) {
      setError(
        t(
          'rechargeTp.walletNotConfigured',
          '钱包连接未配置，请联系管理员设置 VITE_REOWN_PROJECT_ID',
        ),
      );
      return false;
    }
    if (!cfg?.bound_wallet) {
      setError(t('rechargeTp.noBoundWallet', '未绑定 BEP20 钱包，请先绑定'));
      return false;
    }
    if (!isConnected || !address) {
      await wallet.openWalletModal();
      return false;
    }
    if (normAddr(address) !== normAddr(cfg.bound_wallet)) {
      setError(
        t(
          'rechargeTp.wrongBoundWallet',
          '请使用已绑定的 BEP20 钱包地址充值，其他地址转入视为捐赠',
        ),
      );
      return false;
    }
    if (connectedChainId !== TARGET_CHAIN_ID) {
      try {
        await switchChainAsync({ chainId: TARGET_CHAIN_ID });
      } catch (e) {
        setError(
          e instanceof Error
            ? e.message
            : t('rechargeTp.switchChainFailed', '请在钱包中切换到 BSC 网络'),
        );
        return false;
      }
    }
    return true;
  }

  function validateAmount(): string | null {
    const tp = Number(amountTp);
    if (!Number.isFinite(tp) || tp < minTp) {
      return t(`${i18n}.minAmount`, '单次充值最低 {{min}} {{asset}}', {
        min: minTp,
        asset: label,
      });
    }
    if (tp % step !== 0) {
      return t(`${i18n}.stepAmount`, '请按 {{step}} 的整倍数充值 {{asset}}', {
        step,
        asset: label,
      });
    }
    const pay = Number(paymentUsdt);
    const balN = Number(usdtBalance);
    if (Number.isFinite(balN) && pay > balN) {
      return t('rechargeTp.insufficientUsdt', 'USDT 余额不足（需要 {{need}} USDT）', {
        need: paymentUsdt,
      });
    }
    return null;
  }

  async function handleRecharge() {
    if (rechargeInflight.current) {
      await rechargeInflight.current;
      return;
    }

    const run = (async () => {
      setError(null);
      const ready = await ensureWalletReady();
      if (!ready) return;

      if (!cfg?.enabled) {
        setError(t(`${i18n}.disabled`, '{{asset}} 链上充值暂未开放', { asset: label }));
        return;
      }
      if (!cfg.wallet?.address || !cfg.usdt_contract || cfg.usdt_decimals == null) {
        setError(t('rechargeTp.noWallet', '后台未配置充值钱包，请联系客服'));
        return;
      }
      if (!walletClient || !address || !publicClient) {
        setError(t('rechargeTp.walletClientMissing', '钱包未就绪，请重新连接后再试'));
        return;
      }

      const validationErr = validateAmount();
      if (validationErr) {
        setError(validationErr);
        return;
      }

      const token = cfg.usdt_contract as Address;
      const to = cfg.wallet.address as Address;
      const amountRaw = parseUnits(paymentUsdt, cfg.usdt_decimals);

      try {
        setIsSending(true);
        let orderId = pendingOrderId;
        if (orderId == null) {
          orderId = await createPendingOrder();
          setPendingOrderId(orderId);
        }

        const writeKey = `${asset}-recharge:${orderId}`;
        let writePromise = inflightUsdtWrites.get(writeKey);
        if (!writePromise) {
          writePromise = walletClient.writeContract({
            address: token,
            abi: erc20Abi,
            functionName: 'transfer',
            args: [to, amountRaw],
            account: address,
            chain: bsc,
          });
          inflightUsdtWrites.set(writeKey, writePromise);
          void writePromise.finally(() => {
            if (inflightUsdtWrites.get(writeKey) === writePromise) {
              inflightUsdtWrites.delete(writeKey);
            }
          });
        }

        setIsListening(true);
        const startBlock = await publicClient.getBlockNumber();
        const hash = await waitForUsdtTransferOrHash({
          writePromise,
          publicClient,
          token,
          from: address,
          to,
          minRaw: amountRaw,
          startBlock: startBlock > 30n ? startBlock - 30n : 0n,
          timeoutMessage: t(
            'rechargeTp.waitTimeout',
            '等待钱包确认超时。若已在钱包确认并上链，请粘贴交易哈希提交入账',
          ),
        });
        setTxHash(hash as Hash);
        // Backend verifies receipt via Infura — do not wait on wagmi receipt hook.
        await submitHash(hash as Hash, orderId);
      } catch (e) {
        setShowHashRescue(true);
        setError(
          e instanceof Error
            ? e.message
            : t('rechargeTp.transferFailed', '链上转账失败'),
        );
      } finally {
        setIsSending(false);
        setIsListening(false);
      }
    })();

    rechargeInflight.current = run;
    try {
      await run;
    } finally {
      if (rechargeInflight.current === run) rechargeInflight.current = null;
    }
  }

  async function handlePasteSubmit() {
    setError(null);
    const raw = manualTxHash.trim();
    if (!/^0x[a-fA-F0-9]{64}$/.test(raw)) {
      setError(t('rechargeTp.badTxHash', '请输入正确的 0x 交易哈希（66 位）'));
      return;
    }
    const ready = await ensureWalletReady();
    if (!ready || !address) return;
    try {
      let orderId = pendingOrderId;
      if (orderId == null) {
        orderId = await createPendingOrder();
        setPendingOrderId(orderId);
      }
      await submitHash(raw as Hash, orderId);
    } catch {
      /* error set in mutation */
    }
  }

  const primaryLabel = !isConnected
    ? t('rechargeTp.connectWallet', '连接钱包 (TokenPocket / MetaMask)')
    : wrongChain
      ? t('rechargeTp.switchNetwork', '切换到 BSC 网络')
      : isListening
        ? t('rechargeTp.listening', '等待钱包确认，正在监听链上…')
        : isSending
          ? t('rechargeTp.signing', '请在钱包中确认…')
          : isConfirming
              ? t(`${i18n}.confirming`, '确认入账中…')
            : t(`${i18n}.confirmButton`, '确认充值');

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader
          title={t(`${i18n}.title`, '充值 {{asset}}', { asset: label })}
          onBack={() => navigate(-1)}
          theme={C}
          action={t(`${i18n}.records`, '充值记录')}
          onAction={() => navigate(`/recharge-${asset}-records`)}
        />

        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px] pb-28">
          {!tpRechargeEligible || levelGateBlocked ? (
            <ThemedNotice tone="error">
              {t(`${i18n}.levelRequired`, '充值 {{asset}} 需 A3 及以上服务级别（账户类别）方可使用；投资星级不作为门槛', { asset: label })}
            </ThemedNotice>
          ) : null}
          <ThemedNotice tone="warning">
            <ol className="list-decimal space-y-1 pl-4 text-xs leading-relaxed">
              <li>
                {t(
                  `${i18n}.rule1`,
                  '空间站需使用初次绑定的币安链(BEP20)地址充值 USDT，自动到账 {{asset}}',
                  { asset: label },
                )}
              </li>
              <li>
                {t(
                  `${i18n}.rule2`,
                  '其他地址转入 USDT 视为捐赠，无法找回',
                )}
              </li>
              <li>
                {t(
                  `${i18n}.rule3`,
                  '单次充值最低 {{min}} {{asset}}，按 {{step}} 的整倍数充值',
                  { min: minTp, step, asset: label },
                )}
              </li>
              <li>{t(`${i18n}.rule4`, '转账金额会随 USDT 汇率调整')}</li>
            </ol>
          </ThemedNotice>

          <ThemedPanel className="p-4" theme={C}>
            <div className="text-sm" style={{ color: C.muted }}>
              {t(`${i18n}.holdAmount`, '持有 {{asset}} 数量', { asset: label })}{' '}
              <span className="font-mono font-semibold" style={{ color: C.text }}>
                {fmtNum(assetBalance)}
              </span>
            </div>
          </ThemedPanel>

          <ThemedPanel className="p-4" theme={C}>
            <ThemedField
              label={t(`${i18n}.amountLabel`, '充值 {{asset}} 数量 (单次充值最低 {{min}} {{asset}})', {
                min: minTp,
                asset: label,
              })}
            >
              <ThemedInput
                type="number"
                min={minTp}
                step={step}
                value={amountTp}
                onChange={(e) => setAmountTp(e.target.value)}
                placeholder={t('rechargeTp.amountPlaceholder', '请输入充值数量')}
              />
            </ThemedField>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {(asset === 'fc' ? FC_PRESETS : PRESETS).map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setAmountTp(preset)}
                  className="h-9 rounded-xl border text-xs font-semibold"
                  style={{
                    borderColor: amountTp === preset ? `${C.gold}77` : C.softBorder,
                    color: amountTp === preset ? C.gold : C.muted,
                    background: C.inputBg,
                  }}
                >
                  {preset}
                </button>
              ))}
            </div>
            <div className="mt-3 text-sm font-semibold" style={{ color: '#F74E5E' }}>
              {t(`${i18n}.rateLine`, '充值汇率 1{{asset}} = {{rate}}USDT', {
                rate: fmtNum(rate, 4),
                asset: label,
              })}
            </div>
          </ThemedPanel>

          <ThemedPanel className="space-y-2 p-4 text-sm" theme={C}>
            <div className="flex justify-between gap-3">
              <span style={{ color: C.muted }}>{t('rechargeTp.currentId', '当前 ID')}</span>
              <span className="font-mono font-semibold" style={{ color: C.text }}>
                {cfg?.user_id ?? '—'}
              </span>
            </div>
            <div className="flex justify-between gap-3">
              <span style={{ color: C.muted }}>{t('rechargeTp.currentUsername', '当前用户名')}</span>
              <span className="font-semibold" style={{ color: C.text }}>
                {cfg?.username ?? '—'}
              </span>
            </div>
          </ThemedPanel>

          <ThemedPanel className="grid grid-cols-2 gap-3 p-4 text-sm" theme={C}>
            <div>
              <div style={{ color: C.muted }}>{t('rechargeTp.payAmount', '金额')}</div>
              <div className="mt-1 font-mono font-semibold" style={{ color: C.text }}>
                {fmtNum(paymentUsdt)} USDT
              </div>
            </div>
            <div className="text-right">
              <div style={{ color: C.muted }}>{t('rechargeTp.myUsdt', '我的')}</div>
              <div className="mt-1 font-mono font-semibold" style={{ color: C.text }}>
                {fmtNum(usdtBalance)} USDT
              </div>
            </div>
          </ThemedPanel>

          {!isConnected ? (
            <ThemedNotice tone="warning">
              {t(
                'rechargeTp.connectHint',
                '请先连接已绑定的 BEP20 钱包（TokenPocket / MetaMask）',
              )}
            </ThemedNotice>
          ) : cfg?.bound_wallet && address && normAddr(address) !== normAddr(cfg.bound_wallet) ? (
            <ThemedNotice tone="error">
              {t('rechargeTp.wrongBoundWallet', '请使用已绑定的 BEP20 钱包地址充值')}
            </ThemedNotice>
          ) : (
            <ThemedPanel className="p-3 text-xs" theme={C}>
              <span style={{ color: C.muted }}>
                {t('rechargeTp.connected', '已连接')}: {address?.slice(0, 6)}…{address?.slice(-4)}
              </span>
            </ThemedPanel>
          )}

          {txHash ? (
            <ThemedPanel className="p-4 text-xs" theme={C}>
              <div style={{ color: C.muted }}>Tx</div>
              <div className="mt-1 break-all font-mono" style={{ color: C.text }}>
                {txHash}
              </div>
              {isConfirming ? (
                <div className="mt-2" style={{ color: C.gold }}>
                  {t('rechargeTp.confirming', '确认入账中…')}
                </div>
              ) : null}
            </ThemedPanel>
          ) : null}

          {showHashRescue && !confirmMut.isSuccess ? (
            <ThemedPanel className="space-y-3 p-4" theme={C}>
              <div className="text-xs leading-relaxed" style={{ color: C.muted }}>
                {t(
                  'rechargeTp.hashRescueHint',
                  '钱包已确认但页面未入账时：在钱包/BscScan 复制交易哈希，粘贴后提交（可重复充值，每笔哈希各入账一次）',
                )}
              </div>
              <ThemedField label={t('rechargeTp.txHashLabel', '交易哈希')}>
                <ThemedInput
                  value={manualTxHash}
                  onChange={(e) => setManualTxHash(e.target.value)}
                  placeholder="0x…"
                />
              </ThemedField>
              <button
                type="button"
                disabled={confirmMut.isPending || !manualTxHash.trim()}
                onClick={() => void handlePasteSubmit()}
                className="h-10 w-full rounded-xl border text-sm font-semibold disabled:opacity-50"
                style={{
                  background: C.buttonBg,
                  borderColor: C.buttonBorder,
                  color: C.text,
                }}
              >
                {t('rechargeTp.submitHash', '用哈希确认入账')}
              </button>
            </ThemedPanel>
          ) : null}

          {confirmMut.isSuccess ? (
            <ThemedNotice tone="success">
              {t(`${i18n}.success`, '充值成功！已入账 {{amount}} {{asset}}', {
                amount: confirmMut.data?.amount_tp ?? amountTp,
                asset: label,
              })}
            </ThemedNotice>
          ) : null}

          {error ? <ThemedNotice tone="error">{error}</ThemedNotice> : null}
        </div>

        <ThemedBottomActionBar background={BUYFUEL_COLORS.bottomBarBg} className="pb-5 pt-3">
          <button
            type="button"
            disabled={
              levelGateBlocked ||
              (!tpRechargeEligible && !configQuery.isLoading) ||
              isSending ||
              isListening ||
              isConfirming ||
              isSwitching ||
              confirmMut.isSuccess ||
              configQuery.isLoading ||
              (isConnected && !wrongChain && cfg != null && !cfg.enabled)
            }
            onClick={() => void handleRecharge()}
            className="h-11 w-full rounded-xl border text-sm font-semibold disabled:opacity-50"
            style={{
              background: C.buttonBg,
              borderColor: C.buttonBorder,
              color: C.text,
            }}
          >
            {primaryLabel}
          </button>
        </ThemedBottomActionBar>
      </div>
    </div>
  );
}

export function RechargeTp() {
  return <RechargeOnchain asset="tp" />;
}

export function RechargeRp() {
  return <RechargeOnchain asset="rp" />;
}

/** 购买燃料 — 链上 USDT 兑 FC（13 USDT/FC 可调，最小 1 FC，不限服务等级）。 */
export function RechargeFc() {
  return <RechargeOnchain asset="fc" />;
}
